<template>
  <div
    v-if="hasRating"
    class="review-box"
    :class="{ '--emptyRating': !highlightedReviews }"
  >
    <div class="rating-section">
      <div class="rating-score">{{ formattedRating }}</div>
      <div
        class="rating-details"
        :class="{ '--emptyRating': !highlightedReviews }"
      >
        <div class="stars">
          <span
            v-for="star in 5"
            :key="star"
            class="star"
            :class="{
              filled: star <= Math.floor(rating),
              partial: star === Math.ceil(rating) && hasPartialStar,
            }"
            :style="
              star === Math.ceil(rating) && hasPartialStar
                ? { '--fill-percentage': partialStarPercentage + '%' }
                : {}
            "
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.2792 5.28634L8.34652 1.1364C8.21013 0.84353 7.78987 0.84353 7.65348 1.1364L5.72084 5.28634C5.66525 5.4057 5.55104 5.48789 5.41925 5.50336L0.837139 6.04146C0.513769 6.07943 0.383902 6.4753 0.622978 6.69428L4.01065 9.79718C4.10809 9.88642 4.15171 10.0194 4.12585 10.1483L3.22659 14.6308C3.16313 14.9472 3.50312 15.1918 3.78727 15.0343L7.81361 12.8021C7.92942 12.7378 8.07058 12.7378 8.18639 12.8021L12.2127 15.0343C12.4969 15.1918 12.8369 14.9472 12.7734 14.6308L11.8742 10.1483C11.8483 10.0194 11.8919 9.88642 11.9894 9.79718L15.377 6.69428C15.6161 6.4753 15.4862 6.07943 15.1629 6.04146L10.5808 5.50336C10.449 5.48789 10.3347 5.4057 10.2792 5.28634Z"
                fill="currentColor"
              />
            </svg>
          </span>
        </div>
        <div class="rating-text">{{ ratingText }}</div>
      </div>
    </div>
    <div class="reviews-link">
      <button @click="viewReviews" aria-label="Ver opiniones">
        Ver {{ formatedReviewsCount }} opiniones
      </button>
    </div>
  </div>
</template>

<script>
  const RATING_MAP = new Map([
    [5.0, 'Extraordinario'],
    [4.8, 'Excelente'],
    [4.5, 'Fabuloso'],
    [4.2, 'Recomendado'],
    [4.0, 'Muy bueno'],
    [3.0, 'Bueno'],
    [2.0, 'Regular'],
  ]);

  function labelForRating(rating) {
    for (const [min, label] of RATING_MAP) {
      if (rating >= min) return label;
    }
    return 'Malo';
  }

  export default {
    name: 'ReviewBox',
    props: {
      rating: {
        type: Number,
        default: null,
      },
      reviewCount: {
        type: Number,
        default: 0,
      },
      highlightedReviews: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      hasRating() {
        return this.rating !== null && this.rating !== undefined;
      },
      formattedRating() {
        if (!this.hasRating) return '';
        return this.rating.toFixed(1);
      },
      ratingText() {
        if (!this.hasRating) return '';

        return labelForRating(this.rating);
      },
      hasPartialStar() {
        if (!this.hasRating) return false;
        return this.rating % 1 !== 0;
      },
      partialStarPercentage() {
        if (!this.hasRating) return 0;
        return (this.rating % 1) * 100;
      },
      formatedReviewsCount() {
        return this.reviewCount.toLocaleString('es-AR');
      },
    },
    methods: {
      viewReviews() {
        this.$emit('view-reviews');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .review-box {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: end;
    justify-content: space-between;
    background: white;
    padding: 16px;
    border-radius: 20px;
    border: 1px solid #e6e6e6;
    background: #fff;
    min-width: 290px;
    max-width: 412px;
    margin: 0 auto;
    @media (min-width: 768px) {
      margin: 0;
      align-items: center;
      &.--emptyRating {
        max-width: 667px;
        margin: 0;
      }
    }
  }

  .rating-section {
    display: flex;
    align-items: center;
    gap: 4px;
    @media (min-width: 768px) {
      gap: 8px;
    }
  }

  .rating-score {
    font-size: 32px;
    font-weight: bold;
    color: #4d4d4d;
    line-height: 1;
  }

  .rating-details {
    display: flex;
    flex-direction: column;

    @media (min-width: 768px) {
      &.--emptyRating {
        flex-direction: row;
        gap: 16px;
        align-items: center;
      }
    }
  }

  .stars {
    display: flex;
    gap: 2px;
  }

  .star {
    display: inline-flex;
    color: #ddd;
    transition: color 0.2s ease;
    position: relative;
    width: 18px;
    height: 18px;

    svg {
      width: 100%;
      height: 100%;
    }

    &.filled {
      color: #cc9b00;
    }

    &.partial {
      color: #ddd;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: var(--fill-percentage);
        height: 100%;
        overflow: hidden;
        background: linear-gradient(to right, #cc9b00 0%, #cc9b00 100%);
        -webkit-mask-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.2792 5.28634L8.34652 1.1364C8.21013 0.84353 7.78987 0.84353 7.65348 1.1364L5.72084 5.28634C5.66525 5.4057 5.55104 5.48789 5.41925 5.50336L0.837139 6.04146C0.513769 6.07943 0.383902 6.4753 0.622978 6.69428L4.01065 9.79718C4.10809 9.88642 4.15171 10.0194 4.12585 10.1483L3.22659 14.6308C3.16313 14.9472 3.50312 15.1918 3.78727 15.0343L7.81361 12.8021C7.92942 12.7378 8.07058 12.7378 8.18639 12.8021L12.2127 15.0343C12.4969 15.1918 12.8369 14.9472 12.7734 14.6308L11.8742 10.1483C11.8483 10.0194 11.8919 9.88642 11.9894 9.79718L15.377 6.69428C15.6161 6.4753 15.4862 6.07943 15.1629 6.04146L10.5808 5.50336C10.449 5.48789 10.3347 5.4057 10.2792 5.28634Z' fill='currentColor'/%3E%3C/svg%3E");
        mask-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.2792 5.28634L8.34652 1.1364C8.21013 0.84353 7.78987 0.84353 7.65348 1.1364L5.72084 5.28634C5.66525 5.4057 5.55104 5.48789 5.41925 5.50336L0.837139 6.04146C0.513769 6.07943 0.383902 6.4753 0.622978 6.69428L4.01065 9.79718C4.10809 9.88642 4.15171 10.0194 4.12585 10.1483L3.22659 14.6308C3.16313 14.9472 3.50312 15.1918 3.78727 15.0343L7.81361 12.8021C7.92942 12.7378 8.07058 12.7378 8.18639 12.8021L12.2127 15.0343C12.4969 15.1918 12.8369 14.9472 12.7734 14.6308L11.8742 10.1483C11.8483 10.0194 11.8919 9.88642 11.9894 9.79718L15.377 6.69428C15.6161 6.4753 15.4862 6.07943 15.1629 6.04146L10.5808 5.50336C10.449 5.48789 10.3347 5.4057 10.2792 5.28634Z' fill='currentColor'/%3E%3C/svg%3E");
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-size: 18px 18px;
        mask-size: 18px 18px;
      }
    }
  }

  .rating-text {
    font-size: 16px;
    color: #4d4d4d;
    font-weight: 500;

    @media (max-width: 768px) {
      font-weight: 400;
    }
  }

  .reviews-link {
    button {
      color: #5858da;
      cursor: pointer;
      font-size: 16px;
      text-align: right;
      font-weight: 600;
      line-height: 20px;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
      @media screen and (min-width: 375px) {
        text-align: center;
      }
      @media screen and (min-width: 768px) {
        padding: 12px 12px 0 12px;
      }
      &:hover {
        color: #4646b9;
      }
    }
  }

  @media (max-width: 480px) {
    .rating-section {
      justify-content: center;
    }
  }
</style>
